import { useState, useMemo, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import * as XLSX from "xlsx";
import { useAnalyticsData } from "../../../hooks/useAnalyticsData";

interface Props {
  lang: "vi" | "kr";
}

const tt = (vi: string, kr: string, lang: "vi" | "kr") => (lang === "vi" ? vi : kr);

const DEVICE_COLORS: Record<string, string> = {
  desktop: "#004A99",
  mobile: "#00AEEF",
  tablet: "#10B981",
  unknown: "#9ca3af",
};

const BAR_COLORS = [
  "#004A99", "#00AEEF", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#EC4899", "#14B8A6", "#6366F1", "#84CC16",
];

function getQuickRange(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days + 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

function getDefaultRange() {
  return getQuickRange(30);
}

function exportToExcel(filename: string, headers: string[], rows: (string | number)[][]) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function DateRangePicker({
  start,
  end,
  onChange,
  lang,
  onQuickFilter,
}: {
  start: string;
  end: string;
  onChange: (s: string, e: string) => void;
  lang: "vi" | "kr";
  onQuickFilter: (days: number) => void;
}) {
  const quickOptions = [
    { days: 1, vi: "Hôm nay", kr: "오늘" },
    { days: 7, vi: "7 ngày", kr: "7일" },
    { days: 30, vi: "30 ngày", kr: "30일" },
    { days: 90, vi: "90 ngày", kr: "90일" },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-gray-500 font-medium">{tt("Từ", "부터", lang)}</span>
      <input
        type="date"
        value={start}
        max={end}
        onChange={(e) => onChange(e.target.value, end)}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#004A99]/20"
      />
      <span className="text-xs text-gray-500 font-medium">{tt("đến", "까지", lang)}</span>
      <input
        type="date"
        value={end}
        min={start}
        max={new Date().toISOString().slice(0, 10)}
        onChange={(e) => onChange(start, e.target.value)}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#004A99]/20"
      />
      <div className="flex items-center gap-1">
        {quickOptions.map((opt) => (
          <button
            key={opt.days}
            onClick={() => onQuickFilter(opt.days)}
            className="text-xs px-2 py-1.5 bg-gray-100 hover:bg-[#004A99] hover:text-white text-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {tt(opt.vi, opt.kr, lang)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsDashboard({ lang }: Props) {
  const [range, setRange] = useState(getDefaultRange());
  const { data, loading, error, refetch } = useAnalyticsData(range.start, range.end);

  const handleQuickFilter = useCallback((days: number) => {
    setRange(getQuickRange(days));
  }, []);

  const periodDays = useMemo(() => {
    const s = new Date(range.start);
    const e = new Date(range.end);
    return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
  }, [range]);

  const handleExportTraffic = useCallback(() => {
    if (!data) return;
    const headers = ["Date", "Visits", "Unique Visitors", "Page Views"];
    const rows = data.traffic.map((t) => [t.date, t.visits, t.unique, t.pageViews]);
    exportToExcel("Traffic_Stats", headers, rows);
  }, [data]);

  const handleExportTopPages = useCallback(() => {
    if (!data) return;
    const headers = ["Page Path", "Views"];
    const rows = data.topPages.map((p) => [p.page_path, p.views]);
    exportToExcel("Top_Pages", headers, rows);
  }, [data]);

  const handleExportTopProducts = useCallback(() => {
    if (!data) return;
    const headers = ["Product Name", "Slug", "Views"];
    const rows = data.topProducts.map((p) => [p.product_name, p.product_slug, p.views]);
    exportToExcel("Top_Products", headers, rows);
  }, [data]);

  const handleExportAll = useCallback(() => {
    if (!data) return;
    const headers = ["Metric", "Value"];
    const rows = [
      ["Period", `${range.start} → ${range.end}`],
      ["Total Visits", data.totalVisits],
      ["Total Page Views", data.totalPageViews],
      ["Unique Visitors", data.uniqueVisitors],
      ["Avg Session Duration", data.avgSessionDuration],
      ["Bounce Rate", `${data.bounceRate}%`],
      ["Active Users (30min)", data.activeUsers],
      ...Object.entries(data.deviceBreakdown).map(([k, v]) => [`Device: ${k}`, v]),
    ];
    exportToExcel("Analytics_Summary", headers, rows);
  }, [data, range]);

  const handleExportReferrer = useCallback(() => {
    if (!data) return;
    const headers = ["Source", "Sessions"];
    const rows = data.referrerSources.map((r) => [r.source, r.count]);
    exportToExcel("Referrer_Sources", headers, rows);
  }, [data]);

  const handleExportHourly = useCallback(() => {
    if (!data) return;
    const headers = ["Hour", "Views"];
    const rows = data.hourlyTraffic.map((h) => [h.hour, h.count]);
    exportToExcel("Hourly_Traffic", headers, rows);
  }, [data]);

  const handleExportGeo = useCallback(() => {
    if (!data) return;
    const headers = ["Country", "City", "Sessions"];
    const rows = data.geoData.map((g) => [g.country, g.city, g.count]);
    exportToExcel("Geo_Distribution", headers, rows);
  }, [data]);

  const deviceChartData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.deviceBreakdown).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: DEVICE_COLORS[name] || "#9ca3af",
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-[#004A99] animate-spin block mb-3" />
          <p className="text-sm text-gray-500">{tt("Đang tải dữ liệu analytics...", "분석 데이터 로딩 중...", lang)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-center gap-2 text-red-600 mb-2">
          <i className="ri-error-warning-line" />
          <span className="text-sm font-semibold">{tt("Lỗi tải dữ liệu", "데이터 로드 오류", lang)}</span>
        </div>
        <p className="text-xs text-red-500">{error}</p>
        <button onClick={refetch} className="mt-3 text-xs bg-[#004A99] text-white px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap">
          {tt("Thử lại", "재시도", lang)}
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header + Date Range + Quick Filters + Export */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            {tt("Phân tích & Thống kê", "분석 및 통계", lang)}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {tt("Dữ liệu thực tế từ website", "웹사이트 실제 데이터", lang)} · {periodDays} {tt("ngày", "일", lang)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker start={range.start} end={range.end} onChange={setRange} lang={lang} onQuickFilter={handleQuickFilter} />
          <button
            onClick={handleExportAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line" />
            {tt("Xuất Excel", "Excel보내기", lang)}
          </button>
        </div>
      </div>

      {/* Quick Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.metrics.map((m) => (
          <div key={m.key} className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#004A99]/20 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${m.color}15` }}>
                <i className={`${m.icon}`} style={{ color: m.color, fontSize: 14 }} />
              </div>
              <span className="text-xs text-gray-500 font-medium">{tt(m.labelVi, m.labelKr, lang)}</span>
            </div>
            <p className="text-xl font-black text-gray-900">{m.value}</p>
            <p className="text-[10px] text-gray-400 mt-1">{tt(m.subTextVi, m.subTextKr, lang)}</p>
          </div>
        ))}
      </div>

      {/* Period Comparison */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#004A99]/10 flex items-center justify-center">
            <i className="ri-bar-chart-grouped-line text-[#004A99]" style={{ fontSize: 14 }} />
          </div>
          <h3 className="text-sm font-bold text-gray-800">
            {tt("So sánh với kỳ trước", "이전 기간 대비", lang)}
          </h3>
          <span className="text-[10px] text-gray-400 ml-2">
            {tt(`Kỳ trước: ${range.start} → ${range.end}`, `이전 기간: ${range.start} → ${range.end}`, lang)}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            {
              labelVi: "Lượt truy cập",
              labelKr: "방문수",
              current: data.totalVisits.toLocaleString(),
              prev: data.comparison.visits.toLocaleString(),
              change: data.comparison.visitsChange,
              icon: "ri-global-line",
              color: "#004A99",
            },
            {
              labelVi: "Khách duy nhất",
              labelKr: "고유 방문자",
              current: data.uniqueVisitors.toLocaleString(),
              prev: data.comparison.uniqueVisitors.toLocaleString(),
              change: data.comparison.uniqueVisitorsChange,
              icon: "ri-user-line",
              color: "#00AEEF",
            },
            {
              labelVi: "Page views",
              labelKr: "페이지뷰",
              current: data.totalPageViews.toLocaleString(),
              prev: data.comparison.pageViews.toLocaleString(),
              change: data.comparison.pageViewsChange,
              icon: "ri-pages-line",
              color: "#10B981",
            },
            {
              labelVi: "Thời gian TB",
              labelKr: "평균 체류",
              current: data.avgSessionDuration,
              prev: data.comparison.avgSessionDuration,
              change: data.comparison.avgSessionDurationChange,
              icon: "ri-time-line",
              color: "#F59E0B",
            },
            {
              labelVi: "Tỷ lệ thoát",
              labelKr: "이탈률",
              current: `${data.bounceRate}%`,
              prev: data.comparison.bounceRate,
              change: data.comparison.bounceRateChange,
              icon: "ri-arrow-go-back-line",
              color: "#EF4444",
            },
          ].map((item) => {
            const isPositive = item.change.startsWith("+");
            const isNegative = item.change.startsWith("-");
            const isBounce = item.labelVi === "Tỷ lệ thoát";
            const trendColor = isBounce
              ? isNegative
                ? "text-emerald-600 bg-emerald-50"
                : "text-red-600 bg-red-50"
              : isPositive
                ? "text-emerald-600 bg-emerald-50"
                : isNegative
                  ? "text-red-600 bg-red-50"
                  : "text-gray-500 bg-gray-50";
            return (
              <div key={item.labelVi} className="rounded-xl border border-gray-100 p-4 hover:border-gray-200 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                    <i className={`${item.icon}`} style={{ color: item.color, fontSize: 12 }} />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{tt(item.labelVi, item.labelKr, lang)}</span>
                </div>
                <div className="flex items-end gap-2 mb-1">
                  <p className="text-lg font-black text-gray-900">{item.current}</p>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${trendColor}`}>
                    {isPositive && !isBounce && <i className="ri-arrow-up-line mr-0.5" />}
                    {isNegative && !isBounce && <i className="ri-arrow-down-line mr-0.5" />}
                    {isBounce && isNegative && <i className="ri-arrow-down-line mr-0.5" />}
                    {isBounce && isPositive && <i className="ri-arrow-up-line mr-0.5" />}
                    {item.change}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400">
                  {tt("Kỳ trước", "이전 기간", lang)}: <span className="text-gray-600 font-medium">{item.prev}</span>
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chart + Device Breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-gray-800">
              {tt("Lượt truy cập theo thời gian", "시간별 방문자", lang)}
            </h3>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#004A99]" />
                {tt("Tổng truy cập", "총 방문", lang)}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#00AEEF]" />
                {tt("Khách duy nhất", "고유 방문자", lang)}
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.traffic}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  fontSize: 12,
                }}
              />
              <Line type="monotone" dataKey="visits" stroke="#004A99" strokeWidth={2.5} dot={{ r: 3, fill: "#004A99" }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="unique" stroke="#00AEEF" strokeWidth={2} dot={{ r: 3, fill: "#00AEEF" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-3 flex justify-end">
            <button
              onClick={handleExportTraffic}
              className="text-[10px] flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line" />
              {tt("Xuất traffic", "트래픽보내기", lang)}
            </button>
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#004A99]/10 flex items-center justify-center">
              <i className="ri-device-line text-[#004A99]" style={{ fontSize: 14 }} />
            </div>
            <h3 className="text-sm font-bold text-gray-800">
              {tt("Thiết bị truy cập", "접속 기기", lang)}
            </h3>
          </div>
          {deviceChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={deviceChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {deviceChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {deviceChartData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-gray-600">{d.name}</span>
                    </div>
                    <span className="font-bold text-gray-800">{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-xs text-gray-400">
              {tt("Chưa có dữ liệu thiết bị", "기기 데이터 없음", lang)}
            </div>
          )}
        </div>
      </div>

      {/* Top Pages + Top Products */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center">
                <i className="ri-pages-line text-[#00AEEF]" style={{ fontSize: 14 }} />
              </div>
              <h3 className="text-sm font-bold text-gray-800">
                {tt("Trang được xem nhiều", "조회수 높은 페이지", lang)}
              </h3>
            </div>
            <button
              onClick={handleExportTopPages}
              className="text-[10px] flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line" />
              {tt("Xuất", "보내기", lang)}
            </button>
          </div>
          <div className="space-y-3">
            {data.topPages.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">
                {tt("Chưa có dữ liệu trong khoảng thời gian này", "해당 기간 데이터 없음", lang)}
              </p>
            )}
            {data.topPages.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${i < 3 ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-500"}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{p.page_path || "/"}</p>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full rounded-full bg-[#004A99]/60"
                      style={{ width: `${Math.min(100, (p.views / (data.topPages[0]?.views || 1)) * 100)}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs font-bold text-[#004A99] whitespace-nowrap">{p.views.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#004A99]/10 flex items-center justify-center">
                <i className="ri-fire-line text-[#004A99]" style={{ fontSize: 14 }} />
              </div>
              <h3 className="text-sm font-bold text-gray-800">
                {tt("Sản phẩm được xem nhiều", "조회수 높은 제품", lang)}
              </h3>
            </div>
            <button
              onClick={handleExportTopProducts}
              className="text-[10px] flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line" />
              {tt("Xuất", "보내기", lang)}
            </button>
          </div>
          <div className="space-y-3">
            {data.topProducts.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">
                {tt("Chưa có dữ liệu trong khoảng thời gian này", "해당 기간 데이터 없음", lang)}
              </p>
            )}
            {data.topProducts.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${i < 3 ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-500"}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{p.product_name || p.product_slug}</p>
                  <p className="text-[10px] text-gray-400">{p.views.toLocaleString()} views</p>
                </div>
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {tt("Top", "Top", lang)} {i + 1}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Referrer Sources + Hourly Traffic */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Referrer Sources */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <i className="ri-share-forward-line text-[#F59E0B]" style={{ fontSize: 14 }} />
              </div>
              <h3 className="text-sm font-bold text-gray-800">
                {tt("Nguồn traffic", "트래픽 출처", lang)}
              </h3>
            </div>
            <button
              onClick={handleExportReferrer}
              className="text-[10px] flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line" />
              {tt("Xuất", "보내기", lang)}
            </button>
          </div>
          {data.referrerSources.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.referrerSources} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis dataKey="source" type="category" tick={{ fontSize: 10 }} width={100} stroke="#9ca3af" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {data.referrerSources.map((_, idx) => (
                      <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {data.referrerSources.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                      <span className="text-gray-600 truncate max-w-[180px]">{r.source}</span>
                    </div>
                    <span className="font-bold text-gray-800">{r.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-xs text-gray-400">
              {tt("Chưa có dữ liệu nguồn traffic", "트래픽 출처 데이터 없음", lang)}
            </div>
          )}
        </div>

        {/* Hourly Traffic */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <i className="ri-time-line text-[#10B981]" style={{ fontSize: 14 }} />
              </div>
              <h3 className="text-sm font-bold text-gray-800">
                {tt("Thời gian truy cập (theo giờ)", "시간대별 방문", lang)}
              </h3>
            </div>
            <button
              onClick={handleExportHourly}
              className="text-[10px] flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line" />
              {tt("Xuất", "보내기", lang)}
            </button>
          </div>
          {data.hourlyTraffic.some((h) => h.count > 0) ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.hourlyTraffic} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#004A99" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span>
                  {tt("Cao nhất", "최고")}: <strong className="text-gray-800">
                    {data.hourlyTraffic.reduce((max, h) => (h.count > max.count ? h : max), data.hourlyTraffic[0]).hour}
                  </strong>
                </span>
                <span>
                  {tt("Tổng lượt", "총 조회")}: <strong className="text-gray-800">
                    {data.hourlyTraffic.reduce((sum, h) => sum + h.count, 0).toLocaleString()}
                  </strong>
                </span>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-xs text-gray-400">
              {tt("Chưa có dữ liệu theo giờ", "시간대별 데이터 없음", lang)}
            </div>
          )}
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
              <i className="ri-map-pin-line text-[#EF4444]" style={{ fontSize: 14 }} />
            </div>
            <h3 className="text-sm font-bold text-gray-800">
              {tt("Vùng miền & Địa lý", "지역 및 지리", lang)}
            </h3>
          </div>
          <button
            onClick={handleExportGeo}
            className="text-[10px] flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line" />
            {tt("Xuất", "보내기", lang)}
          </button>
        </div>
        {data.geoData.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.geoData.map((g, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${BAR_COLORS[i % BAR_COLORS.length]}15` }}>
                  <i className="ri-map-pin-2-line" style={{ color: BAR_COLORS[i % BAR_COLORS.length], fontSize: 14 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-800 truncate">{g.city}</p>
                  <p className="text-[10px] text-gray-500">{g.country}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-gray-900">{g.count.toLocaleString()}</p>
                  <p className="text-[10px] text-gray-400">{tt("phiên", "세션", lang)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-xs text-gray-400">
            {tt("Chưa có dữ liệu địa lý", "지역 데이터 없음", lang)}
          </div>
        )}
      </div>
    </div>
  );
}